import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActionsBar from '../shared/DialogActionsBar';
import { SECONDARY_BUTTON_SX } from '../shared/dialogActionsSx';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useThemeStore, UI_SIZE_LABELS, UI_SIZE_ORDER } from '../../stores/themeStore';
import { APP_FONTS, loadAppFontPreviews } from '../../theme/appFonts';
import { noteSizeChoice } from '../fun/grannyTrigger';
import { useGlobalStore } from '../../stores/globalStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { useTopToolbar, ToolbarPageActionsSlot } from './TopToolbarContext';
import apiClient, { getRoutedApiHost } from '../../api/client';
import { API } from '../../api/endpoints';
import NotificationBell from './NotificationBell';
import { useNotificationStore } from '../../stores/notificationStore';
import CobrowseButton from '../common/CobrowseButton';
import ReportProblemToolbarButton from '../feedback/ReportProblemToolbarButton';
import { useTourStore } from '../../stores/tourStore';
import { isPsiEmail } from '../../utils/psiStaff';
import RunMigrationsDialog from './RunMigrationsDialog';
import { useFacilitySwitcher } from '../../hooks/useFacilitySwitcher';
import { getRecentFacilities, recordRecentFacility } from '../../utils/recentFacilities';
import { openFacilityWindow, peekHandoff, peekHandoffPage, clearHandoff, resolveAllowed } from '../../utils/facilityWindow';
import { facilityAccentColor } from '../../utils/facilityAccent';
import { facilityMatchesSearch } from '../../utils/facilitySearch';
import { useMealPatternFacilitySettings } from '../../hooks/useFacilitySettings';
import { useMenuCycles } from '../../api/menuCycleApi';
import { useMyDefaultFacility, useSetMyDefaultFacility } from '../../api/usersApi';
import FeatureAccessDialog from '../common/FeatureAccessDialog';
import KeyboardShortcutsDialog from '../common/KeyboardShortcutsDialog';
import { useRegisteredShortcuts, registerShortcuts } from '../../hooks/shortcutRegistry';
import { advanceScreenSummary } from '../common/ScreenSummary';
import GlobalSearch from '../common/GlobalSearch';
import { globalSearchLabel } from '../common/commandPalette';
import { usePermission } from '../../hooks/usePermission';
import { applyUpdate, useUpdateNudge } from '../../utils/appUpdate';
import { hasUnsavedWork } from '../../utils/unsavedWork';
import type { MenuHeader } from '../../types/menuCycle';
import { signOut } from '../../auth/signOut';
import { navBreadcrumbs, sectionCrumb, LEGACY_HUB_REDIRECTS } from '../../navigation/navConfig';
import PageHelpButton from '../help/PageHelpButton';
import { useNavEntrySection } from '../../navigation/navEntry';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useCobrowseStore } from '../../stores/cobrowseStore';
import './TopToolbar.css';
import CheckboxField from '../form/CheckboxField';
import RadioGroupField from '../form/RadioGroupField';
import SelectField from '../../components/form/SelectField';
import { displayEhrTitle } from '../../utils/ehrDisplayName';

/**
 * The bar's single search control.
 *
 * Renders as one object at one size in both of its states so it does not jump
 * as you move between pages:
 *
 *   page filter   an input bound to the page's own query, plus a shortcut chip
 *                 that still opens global search
 *   global only   a button dressed as the same input, whose entire surface
 *                 opens global search
 *
 * The shortcut chip is not decoration — it is the discoverability for Ctrl+K,
 * which was previously only findable by hovering a tooltip on a magnifier.
 */
export function ToolbarSearch({
  placeholder,
  query,
  onQueryChange,
  onOpenGlobal,
  residentPluralLower,
  globalLabel,
}: {
  /** Set by `useToolbarSearch` when the page filters its own content. */
  placeholder?: string;
  query: string;
  onQueryChange: (v: string) => void;
  onOpenGlobal: () => void;
  residentPluralLower: string;
  /** What global search covers, e.g. "Search residents, items, recipes" (globalSearchLabel). */
  globalLabel?: string;
}) {
  const label = globalLabel ?? `Search ${residentPluralLower}`;
  // Mac reads Cmd, everyone else reads Ctrl. `userAgent` rather than the
  // deprecated `platform`, and defaulting to Ctrl if neither matches.
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
  // No Ctrl+K: the magnifier this replaced had a tooltip claiming it, which
  // was wrong, and putting the claim on a visible chip would have promoted a
  // wrong tooltip into a wrong instruction.
  //
  // Ctrl+/ is the shortcut `buildGlobalShortcuts` already declares for search.
  // Nothing ever passed `onOpenSearch`, so it was never registered and search
  // had no shortcut at all \u2014 TopToolbar registers it now.
  const shortcut = isMac ? '\u2318 /' : 'Ctrl /';

  const chip = (
    <Tooltip title={`${label} (${isMac ? '\u2318' : 'Ctrl+'}/)`} arrow>
      <button
        type="button"
        className="tt-search-kbd"
        onClick={(e) => { e.stopPropagation(); onOpenGlobal(); }}
        aria-label={label}
      >
        {shortcut}
      </button>
    </Tooltip>
  );

  if (!placeholder) {
    return (
      <button type="button" className="tt-search tt-search--button" onClick={onOpenGlobal}>
        <i className="bi bi-search tt-search-icon" aria-hidden="true" />
        <span className="tt-search-label">{label}</span>
        <span className="tt-search-kbd" aria-hidden="true">{shortcut}</span>
      </button>
    );
  }

  return (
    <div className="tt-search">
      <i className="bi bi-search tt-search-icon" aria-hidden="true" />
      <input
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        aria-label={placeholder}
        className="tt-search-input"
      />
      {query ? (
        <button
          type="button"
          className="tt-search-clear"
          aria-label="Clear search"
          // Focus stays in the box: on a phone the box is only wide while it
          // has focus, so a blur here would move the button out from under the tap.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onQueryChange('')}
        >
          <i className="bi bi-x-circle" aria-hidden="true" />
        </button>
      ) : chip}
    </div>
  );
}

interface FacilityItem {
  id: number;
  name: string;
  organizationId: string;
  isCorporate: boolean;
}

/** Route-to-label mapping for auto-breadcrumbs (merged with the navigation config's below). */
const staticRouteLabels: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/corporate-dashboard': 'Corporate Dashboard',
  '/pdpm': 'Nutrition Compliance',
  '/admin/status': 'System Status',
  '/admin/feedback': 'Test Kitchen',
  '/overview': 'Overview',
  '/people': 'Residents',
  '/people/new': 'New Resident',
  '/people/deleted': 'Deleted',
  '/people/guests': 'Guests',
  '/menucycle-hub': 'Menu Cycle',
  '/menucycle': 'Menu Cycles',
  '/menucycle/requests': 'Change Requests',
  '/menucycle/request-change': 'Request a Menu Change',
  '/menucycle/recyclebin': 'Recycle Bin',
  '/menucycle/aim': 'Auto Item Mods',
  '/menucycle/excel-diet-orders': 'Excel Diet Orders',
  // Each names itself under Menu Cycles; they said "Menu Cycles", their parent's name. The three
  // with a menu id match by their base path (buildBreadcrumbs' fallback). Names as utils/pageTitles.
  '/menucycle/today': "Today's Menu",
  '/menucycle/analysis': 'Nutrition Analysis',
  '/menucycle/workbench': 'Modify',
  '/menucycle/basic': 'Basic Menu',
  '/nutritional': 'Nutritional',
  '/nutritional-analysis': 'Nutritional Analysis',
  '/nutritional-validation': 'Validation',
  '/intake-studies': 'Intake Study',
  '/special-events': 'Special Events',
  '/tray-line-setup': 'Tray Line Setup',
  // Without it the trail was the section alone ("Kitchen"), read as the page title.
  '/selective-touch': 'Selective Touch',
  '/global-adjustments': 'Global Adjustments',
  '/meal-patterns': 'Meal Pattern Groups',
  // Static fallback only. Facilities rename meal patterns, so the live label
  // comes from `breadcrumbLabelOverrides` below - this entry is what shows
  // before facility settings have loaded.
  '/global-meal-patterns': 'Global Meal Patterns',
  '/weight-entry': 'Weight Entry',
  '/clinical': 'Clinical',
  '/assessments': 'Assessments',
  '/plan-of-care': 'Plan of Care',
  '/food-data': 'Food Data',
  '/items': 'Items',
  '/items/new': 'New Item',
  '/recipes': 'Recipes',
  '/recipes/new': 'New Recipe',
  '/analysis': 'Analysis',
  // The Reports panel's analysis pages. Their parent (Reports) comes from the
  // nav config; without a label of their own the trail was just "Reports",
  // and each page drew its own big title to make up for it.
  '/analysis/diet-orders': 'Diet Orders',
  '/analysis/food-allergies': 'Food Allergies',
  '/analysis/special-needs': 'Special Needs',
  '/analysis/special-devices': 'Adaptive Eating Devices',
  '/analysis/served-meals': 'Served Meals',
  '/analysis/served-items': 'Served Items',
  '/analysis/employee-served-items': 'Employee Served Items',
  '/analysis/cost-menu': 'Menu Cost',
  '/analysis/cost-nourishment': 'Nourishment Cost',
  '/analysis/demand-forecasting': 'Demand Forecasting',
  '/tables': 'Tables',
  '/reports': 'Reports',
  // Reports is a nested router (ReportsLayout). Without these the
  // fallback walk in `buildBreadcrumbs` only ever resolves `/reports`,
  // which is a single crumb — and a single crumb renders as a plain
  // page title, so every page under /reports said just "Reports".
  // The report *runner* (`/reports/run/:slug`) is not listed here:
  // its label is the report's name, which only the form knows, so it
  // pushes its own crumbs via <PageHeader>. `/reports/all` is also
  // absent on purpose — it is the same catalog as `/reports`, so it
  // falls back to the single "Reports" crumb rather than saying
  // "Reports > All Reports" about one page.
  '/reports/print-queue': 'Print Queue',
  '/reports/scheduled': 'Scheduled Reports',
  // Reports > Surveys, and its two sub-pages under it. The template and session pages push their
  // own trail ending in the record's name (pages/surveys/surveyCrumbs.ts).
  '/surveys': 'Surveys',
  '/surveys/sessions': 'Sessions',
  '/surveys/analysis': 'Analysis',
  '/operations': 'Operations',
  '/bulk-order': 'Bulk Order',
  '/dining-rooms': 'Dining Rooms',
  '/employees': 'Employees',
  '/workflows': 'Workflows',
  '/emr': 'EMR/EHR',
  '/ordering': 'Purchasing',
  '/ordering/orders': 'Purchase Orders',
  '/ordering/orders/new': 'New Purchase Order',
  '/ordering/order-guides': 'Order Guides',
  '/ordering/reorder': 'Reorder',
  '/ordering/requisitions': 'Requisitions',
  '/ordering/requisitions/new': 'New Requisition',
  '/ordering/demand': 'Demand Planning',
  '/ordering/receiving': 'Receiving',
  '/ordering/invoices': 'Vendor Invoices',
  '/ordering/invoices/new': 'New Invoice',
  '/ordering/credit-memos': 'Credit Memos',
  '/ordering/credit-memos/new': 'New Credit Memo',
  '/ordering/inventory': 'Inventory',
  '/ordering/inventory/items': 'Items',
  '/ordering/inventory/items/new': 'New Item',
  '/ordering/inventory/transactions': 'Transactions',
  '/ordering/inventory/counts': 'Counts',
  '/ordering/inventory/floor-stock': 'Floor Stock',
  '/ordering/inventory-planning': 'Inventory Planning',
  '/ordering/waste': 'Waste',
  '/purchasing/suppliers': 'Suppliers',
  '/ordering/suppliers': 'Supplier Accounts',
  '/ordering/suppliers/new': 'New Supplier',
  '/ordering/item-links': 'Item Links',
  '/ordering/catalog': 'Price Updates',
  '/ordering/contracts': 'Contracts',
  '/ordering/contracts/new': 'New Contract',
  '/ordering/pricing-import': 'Price Updates',
  '/ordering/contacts': 'Contacts',
  '/ordering/edi': 'EDI',
  '/ordering/costing': 'Costing',
  '/ordering/prep-schedule': 'Prep Schedule',
  '/ordering/allergens': 'Allergens',
  '/ordering/spend': 'Spend',
  '/ordering/meal-cost': 'Meal Cost',
  '/ordering/scorecard': 'Supplier Scorecard',
  '/ordering/corporate': 'Corporate Purchasing',
  '/ordering/setup': 'Setup',
  '/purchasing/departments': 'Departments',
  '/purchasing/ledgers': 'Ledgers',
  '/facilities': 'Facilities',
  '/users': 'Users',
  '/users/new': 'New User',
  '/users/deleted': 'Deleted Users',
  '/users/logged-in': 'Logged In Users',
  '/roles': 'Roles',
  '/roles/new': 'New Role',
  '/printers': 'Printers',
  '/print-schedules': 'Print Schedules',
  '/unit-rooms': 'Unit Rooms',
  '/census': 'Census',
  '/diet-config': 'Diet Configuration',
  '/system-log': 'System Log',
  '/help': 'Help',
};

/** Parent route mapping for breadcrumb hierarchy (merged with the navigation config's below). */
const staticRouteParents: Record<string, string> = {
  '/menucycle': '/menucycle-hub',
  '/menucycle/requests': '/menucycle-hub',
  '/menucycle/request-change': '/menucycle-hub',
  '/menucycle/recyclebin': '/menucycle-hub',
  '/menucycle/aim': '/menucycle-hub',
  '/menucycle/excel-diet-orders': '/menucycle-hub',
  '/menucycle/today': '/menucycle',
  '/menucycle/analysis': '/menucycle',
  '/menucycle/workbench': '/menucycle',
  '/menucycle/basic': '/menucycle',
  '/nutritional-analysis': '/nutritional',
  '/nutritional-validation': '/nutritional',
  '/intake-studies': '/clinical-nutrition',
  '/special-events': '/nutritional',
  '/tray-line-setup': '/nutritional',
  '/global-adjustments': '/nutritional',
  '/meal-patterns': '/nutritional',
  '/global-meal-patterns': '/nutritional',
  '/weight-entry': '/nutritional',
  '/assessments': '/clinical',
  '/plan-of-care': '/clinical',
  '/items': '/food-data',
  '/items/new': '/items',
  '/recipes': '/food-data',
  '/recipes/new': '/recipes',
  '/bulk-order': '/operations',
  '/dining-rooms': '/operations',
  '/employees': '/operations',
  '/ordering/orders': '/ordering',
  '/ordering/orders/new': '/ordering/orders',
  '/ordering/order-guides': '/ordering',
  '/ordering/reorder': '/ordering',
  '/ordering/requisitions': '/ordering',
  '/ordering/requisitions/new': '/ordering/requisitions',
  '/ordering/demand': '/ordering',
  '/ordering/receiving': '/ordering',
  '/ordering/invoices': '/ordering',
  '/ordering/invoices/new': '/ordering/invoices',
  '/ordering/credit-memos': '/ordering',
  '/ordering/credit-memos/new': '/ordering/credit-memos',
  '/ordering/inventory': '/ordering',
  '/ordering/inventory/items': '/ordering/inventory',
  '/ordering/inventory/items/new': '/ordering/inventory/items',
  '/ordering/inventory/transactions': '/ordering/inventory',
  '/ordering/inventory/counts': '/ordering/inventory',
  '/ordering/inventory/floor-stock': '/ordering/inventory',
  '/ordering/inventory-planning': '/ordering',
  '/ordering/waste': '/ordering',
  '/purchasing/suppliers': '/ordering',
  '/ordering/suppliers': '/ordering',
  '/ordering/suppliers/new': '/ordering/suppliers',
  '/ordering/item-links': '/ordering',
  // Price Updates' Compare tab: the page lives at /ordering/pricing-import in the menu.
  '/ordering/catalog': '/section/purchasing',
  '/ordering/contracts': '/ordering',
  '/ordering/contracts/new': '/ordering/contracts',
  '/ordering/pricing-import': '/ordering',
  '/ordering/contacts': '/ordering/suppliers',
  '/ordering/edi': '/ordering',
  '/ordering/costing': '/ordering',
  '/ordering/prep-schedule': '/ordering',
  '/ordering/allergens': '/ordering',
  '/ordering/spend': '/ordering',
  '/ordering/meal-cost': '/ordering',
  '/ordering/scorecard': '/ordering',
  '/ordering/corporate': '/ordering',
  '/ordering/setup': '/ordering',
  '/purchasing/departments': '/ordering',
  '/purchasing/ledgers': '/ordering',
  '/users/new': '/users',
  // Deleted Users is a Setup page of its own (Setup > Deleted Users), as Logged In Users is; it
  // was a level deeper, under Users.
  '/roles/new': '/roles',
  '/people/new': '/people',
  '/people/deleted': '/people',
  '/people/guests': '/people',
  '/reports/print-queue': '/reports',
  '/reports/scheduled': '/reports',
  '/surveys/sessions': '/surveys',
  '/surveys/analysis': '/surveys',
  // The Analysis page's topics: Reports > Analysis > Diet Orders.
  '/analysis/diet-orders': '/analysis',
  '/analysis/food-allergies': '/analysis',
  '/analysis/special-needs': '/analysis',
  '/analysis/special-devices': '/analysis',
  '/analysis/served-meals': '/analysis',
  '/analysis/served-items': '/analysis',
  '/analysis/employee-served-items': '/analysis',
};

/**
 * Each page sits under its section's overview (Menus > Menu Cycles), from navigation/navConfig.ts.
 * A static parent wins only where the page really is below it (Items > New Item, Reports > Print
 * Queue); parents that were the old hubs are dropped, since those addresses now redirect.
 */
const NAV_CRUMBS = navBreadcrumbs();
const routeLabels: Record<string, string> = Object.fromEntries(
  Object.entries({ ...staticRouteLabels, ...NAV_CRUMBS.labels }).filter(([route]) => !(route in LEGACY_HUB_REDIRECTS)),
);
const routeParents: Record<string, string> = { ...NAV_CRUMBS.parents };
for (const [route, parent] of Object.entries(staticRouteParents)) {
  if (parent in LEGACY_HUB_REDIRECTS) continue;
  if (route.startsWith(parent + '/') || !(route in routeParents)) routeParents[route] = parent;
}

interface BreadcrumbEntry {
  label: string;
  path?: string;
  /** Set by context-pushed crumbs that guard their own navigation. */
  onClick?: () => void;
}

export function buildBreadcrumbs(
  pathname: string,
  // Per-render overrides that win over the static `routeLabels`.
  // Used for routes whose label comes from facility settings
  // (e.g. `/emr` → the customer's EHR title). Keyed by full path.
  labelOverrides: Record<string, string> = {},
  // Per-render parents that win over `routeParents`: a page listed in two sections sits under the
  // one the user opened it from (navigation/navEntry.ts).
  parentOverrides: Record<string, string> = {},
): BreadcrumbEntry[] {
  const crumbs: BreadcrumbEntry[] = [];
  const labelFor = (p: string): string | undefined =>
    labelOverrides[p] ?? routeLabels[p];
  const parentOf = (p: string): string | undefined => parentOverrides[p] ?? routeParents[p];

  // Walk up the parent chain
  let current: string | undefined = pathname;
  const visited = new Set<string>();

  while (current && !visited.has(current)) {
    visited.add(current);
    const label = labelFor(current);
    if (label) {
      crumbs.unshift({ label, path: current });
    }
    current = parentOf(current);
  }

  // For dynamic routes (e.g. /people/:id), try to match the base
  if (crumbs.length === 0) {
    const segments = pathname.split('/').filter(Boolean);
    for (let i = segments.length - 1; i >= 1; i--) {
      const partial = '/' + segments.slice(0, i).join('/');
      const partialLabel = labelFor(partial);
      if (partialLabel) {
        crumbs.unshift({ label: partialLabel, path: partial });
        let parent = parentOf(partial);
        let parentLabel = parent ? labelFor(parent) : undefined;
        while (parent && parentLabel) {
          crumbs.unshift({ label: parentLabel, path: parent });
          parent = parentOf(parent);
          parentLabel = parent ? labelFor(parent) : undefined;
        }
        break;
      }
    }
  }

  // Remove path from last crumb (it's the current page)
  if (crumbs.length > 0) {
    crumbs[crumbs.length - 1] = { label: crumbs[crumbs.length - 1].label };
  }

  return crumbs;
}

interface TopToolbarProps {
  onMenuClick?: () => void;
}

/** The user menu's Mode choice: the shared radio group, an icon beside each word. */
const THEME_MODE_OPTIONS = [
  { value: false, label: <><i className="bi bi-sun tt-s30" aria-hidden="true" /> Light</> },
  { value: true, label: <><i className="bi bi-moon tt-s30" aria-hidden="true" /> Dark</> },
] as const;

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);

/** Hint shown the first few times someone reaches for Ctrl+F. */
const VG_HINT_KEY = 'dietech_virtualgrid_findhint';
const VG_HINT_LIMIT = 3;

/**
 * Explains, briefly and only a few times, why Ctrl+F was taken over.
 *
 * Silently hijacking a browser shortcut is worse than the problem it solves if the user never
 * learns what happened — they just think Find is broken. After a few showings the behaviour is
 * learned and the toast stops.
 */
function showVirtualGridSearchHint() {
  try {
    const seen = Number(localStorage.getItem(VG_HINT_KEY) ?? '0');
    if (seen >= VG_HINT_LIMIT) return;
    localStorage.setItem(VG_HINT_KEY, String(seen + 1));
  } catch {
    // Private browsing — show the hint rather than suppress it.
  }

  useNotificationStore.getState().addNotification({
    message:
      'Search opened instead of browser Find. Long lists only load the rows you can see, so Find would miss people further down.',
    type: 'info',
    duration: 7000,
  });
}

export default function TopToolbar({ onMenuClick }: TopToolbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { darkMode, setDarkMode, themeSize, setThemeSize, highContrast, setHighContrast, font, setFont } =
    useThemeStore();
  const { facilityId, facilityName, selectFacility } = useGlobalStore();
  const userId = useGlobalStore((s) => s.userId);
  // Facilities rename "Residents" (to Patients, Clients, ...); the search
  // placeholder has to follow, or the one search box in the bar tells half
  // the customers it searches something their site does not have.
  const residentPluralLabel = useGlobalStore((s) => s.residentPluralLabel);
  const residentPluralLower = residentPluralLabel.toLowerCase();
  // Items and recipes join the search for users whose role can open them.
  const { itemsLevel, recipeLevel, roleLoaded } = usePermission();
  const { breadcrumbs: contextBreadcrumbs, searchQuery, setSearchQuery, searchPlaceholder } = useTopToolbar();
  // Phones: Report a problem, Support and the Temp Menu chip move to the More sheet
  // (MobileMoreMenu), or the bar's controls cover the menu button at 360px.
  const { isMobile } = useIsMobile();
  // A live support session stays in the bar, so it is plain that someone can see the screen.
  const supportSessionLive = useCobrowseStore((s) => s.status === 'active');

  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [facilityAnchor, setFacilityAnchor] = useState<HTMLElement | null>(null);
  const [facilitySearch, setFacilitySearch] = useState('');
  const [facilities, setFacilities] = useState<FacilityItem[]>([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [facilitiesError, setFacilitiesError] = useState<string | null>(null);
  /** Open when an override is already set, so clearing it is one click. */
  const [isTempMenuOpen, setIsTempMenuOpen] = useState(
    () => useGlobalStore.getState().tempMenuOverrideId != null,
  );
  const [aboutOpen, setAboutOpen] = useState(false);
  const [featureAccessOpen, setFeatureAccessOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // Reads what is actually bound right now rather than a list maintained by
  // hand beside the bindings. That list had drifted twice — see
  // shortcutRegistry.ts.
  const documentedShortcuts = useRegisteredShortcuts();
  const [isDebugSubmenuOpen, setIsDebugSubmenuOpen] = useState(false);
  // Run Migrations opens its dialog: Preview first, then Run for the ticked scripts.
  const [migrationsOpen, setMigrationsOpen] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const facilitySearchRef = useRef<HTMLInputElement>(null);

  const tempMenuOverrideId = useGlobalStore((s) => s.tempMenuOverrideId);
  const tempMenuOverrideName = useGlobalStore((s) => s.tempMenuOverrideName);
  const setTempMenuOverride = useGlobalStore((s) => s.setTempMenuOverride);
  const clearTempMenuOverride = useGlobalStore((s) => s.clearTempMenuOverride);
  const { data: menuCycles } = useMenuCycles(facilityId);
  // Filter to top-level, non-deleted menus (fKey_Menu_Hdr null = not a holiday sub-menu)
  const availableMenus = useMemo(
    () => (menuCycles ?? []).filter((m: MenuHeader) => !m.fKey_Menu_Hdr && !m.isDeleted),
    [menuCycles]
  );
  // The Temp Menu must be one of this facility's menus: one restored after a reload, one picked before
  // someone deleted it, or one carried over from the facility the user switched from (menus can be
  // shared between facilities, so it is kept when this facility uses it). Otherwise drop it and say why.
  useEffect(() => {
    if (tempMenuOverrideId == null || !Array.isArray(menuCycles)) return;
    const { tempMenuCarriedFrom, tempMenuOverrideName: menuName, facilityName: openFacility, confirmTempMenuCarriedOver } =
      useGlobalStore.getState();
    if (availableMenus.some((m: MenuHeader) => m.pKey_Menu_Hdr === tempMenuOverrideId)) {
      if (tempMenuCarriedFrom != null) confirmTempMenuCarriedOver();
      return;
    }
    clearTempMenuOverride();
    const menuLabel = menuName ? `the ${menuName} menu` : 'that menu';
    useNotificationStore.getState().addNotification({
      message: tempMenuCarriedFrom != null
        ? `Temp Menu turned off. ${openFacility || 'This facility'} does not use ${menuLabel}.`
        : `Temp Menu turned off. ${menuLabel.charAt(0).toUpperCase()}${menuLabel.slice(1)} is no longer available.`,
      type: 'warning',
      duration: 8000,
    });
  }, [tempMenuOverrideId, menuCycles, availableMenus, clearTempMenuOverride]);

  const userFirstName = useGlobalStore((s) => s.userFirstName);
  const userLastName = useGlobalStore((s) => s.userLastName);
  const globalUserName = useGlobalStore((s) => s.userName);
  const userRole = useGlobalStore((s) => s.userRole);
  const userName = userFirstName && userLastName
    ? `${userFirstName} ${userLastName}`
    : globalUserName || auth.user?.profile?.preferred_username || 'User';
  const userInitials = userFirstName && userLastName
    ? `${userFirstName[0]}${userLastName[0]}`.toUpperCase()
    : (globalUserName?.[0] ?? 'U').toUpperCase();
  const userEmail = auth.user?.profile?.email || '';
  const facilityOpen = Boolean(facilityAnchor);
  const userMenuOpen = Boolean(userMenuAnchor);
  // Font names draw in their own face: fetch the tiny name-only subsets
  // once the menu that shows them opens.
  useEffect(() => {
    if (userMenuOpen) loadAppFontPreviews();
  }, [userMenuOpen]);
  // A new build has waited all day on this screen (it normally loads on the
  // next section change, see utils/appUpdate.ts): offer it here, quietly.
  const updateNudge = useUpdateNudge();
  const clearConnection = useConnectionStore((s) => s.clearConnection);
  const selectedConnection = useConnectionStore((s) => s.selectedConnection);
  const { switchFacility } = useFacilitySwitcher();

  // Unsaved changes confirmation state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingFacility, setPendingFacility] = useState<FacilityItem | null>(null);

  // Facility-configured EMR/EHR title — case-blind lookup matches
  // EhrTab.tsx / PccHistoryDialog / Sidebar. Falls back to "EMR"
  // when the facility hasn't set one. Passed as a per-render
  // breadcrumb override so the `/emr` crumb (and its parent chain)
  // renders with the customer's terminology.
  const ehrTitle = useGlobalStore((s) => {
    const fs = s.facilitySettings as Record<string, unknown> | null;
    const raw = fs?.eHR_Title as string | undefined;
    return displayEhrTitle(raw) || 'EMR';
  });
  // Facilities rename meal patterns (to Diet Plan, Tray Card, ...), so the
  // Global Meal Pattern crumb has to follow the same wording the sidebar
  // entry uses — both build it as `Global {mealPatternShortDescription}`.
  const { settings: mealPatternSettings } = useMealPatternFacilitySettings();
  const globalMealPatternLabel = `Global ${mealPatternSettings.mealPatternShortDescription}`;

  // Facility Settings is /facilities/<id>: its menu entry is this facility's, so on this facility it
  // sits under Setup as the menu has it; another facility's settings (opened from Facilities) sit
  // under Facilities, the entry the sidebar highlights for them.
  const facilitySettingsPath = /^\/facilities\/\d+$/.test(location.pathname) ? location.pathname : null;
  const onOwnFacility = facilitySettingsPath === `/facilities/${facilityId}`;

  const breadcrumbLabelOverrides = useMemo(
    (): Record<string, string> => ({
      '/emr': ehrTitle,
      // Facilities rename Residents, and the breadcrumb has to follow — the
      // static table cannot know the site calls them Patients. These are the
      // menu's own labels (navConfig dynamicLabel), word for word.
      '/people': residentPluralLabel,
      '/people/deleted': `Manage ${residentPluralLabel}`,
      '/section/residents': residentPluralLabel,
      '/global-meal-patterns': globalMealPatternLabel,
      ...(facilitySettingsPath ? { [facilitySettingsPath]: 'Facility Settings' } : {}),
    }),
    [ehrTitle, residentPluralLabel, globalMealPatternLabel, facilitySettingsPath],
  );

  // Selective Touch opened from Residents sits under Residents, not its home Kitchen.
  const navEntry = useNavEntrySection();
  const breadcrumbParentOverrides = useMemo(
    (): Record<string, string> => ({
      ...(navEntry ? { [navEntry.pageKey]: sectionCrumb(navEntry.section) } : {}),
      ...(facilitySettingsPath ? { [facilitySettingsPath]: onOwnFacility ? '/settings' : '/facilities' } : {}),
    }),
    [navEntry, facilitySettingsPath, onOwnFacility],
  );

  // Auto-generate breadcrumbs from route, or use context overrides
  const breadcrumbs = contextBreadcrumbs.length > 0
    ? contextBreadcrumbs
    : buildBreadcrumbs(location.pathname, breadcrumbLabelOverrides, breadcrumbParentOverrides);

  const pageTitle = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].label : '';

  // Load associated facilities
  const loadFacilities = async () => {
    if (userId <= 0 || facilityId <= 0) return;
    setLoadingFacilities(true);
    setFacilitiesError(null);
    try {
      const url = API.facilityAssociated(facilityId, userId);
      const { data } = await apiClient.get(url);
      const mapped = (data as Record<string, unknown>[]).map((f: Record<string, unknown>) => ({
        id: (f.pKey_Facility ?? f.PKey_Facility ?? 0) as number,
        name: (f.facility_Name ?? f.Facility_Name ?? '') as string,
        organizationId: String((f.pKey_Facility ?? f.PKey_Facility ?? 0)),
        isCorporate: Boolean(f.isCorporate ?? f.IsCorporate ?? false),
      }));
      setFacilities(mapped);
      return mapped;
    } catch (err) {
      console.error('[FacilityDropdown] Failed to fetch facilities:', err);
      setFacilitiesError('Failed to load facilities');
      return [];
    } finally {
      setLoadingFacilities(false);
    }
  };

  // The facility a re-resolve was last asked for (see the `!match` branch below).
  const reResolveAskedRef = useRef<string | null>(null);
  useEffect(() => {
    if (userId <= 0 || facilityId <= 0) return;
    let cancelled = false;
    const init = async () => {
      const loaded = await loadFacilities();
      if (cancelled || !loaded || loaded.length === 0) return;

      // A window opened by "open in a new window" asks for a facility;
      // `main.tsx` parked the request and `ConnectionSelection` already vetted
      // its connection. `loaded` is the server's associated-facility list for
      // THIS user, so honouring the request only when it appears here is the
      // security check — an unknown id is dropped and they land on their
      // default facility.
      //
      // The connection is re-checked rather than assumed: a request parked for
      // one organization must not be applied inside another, where the same
      // number is a different facility entirely.
      //
      // Cleared either way, and only after the `cancelled` guard so
      // StrictMode's discarded first mount does not eat it: the request applies
      // to this arrival, and must not re-fire when this effect runs again after
      // a later manual switch.
      const handoff = peekHandoff();
      if (handoff.facilityId) {
        // A link made for one page (Tech Log's "Draft a menu with AI") opens it only on the facility
        // it asked for: never on whatever facility a refused request falls back to.
        const page = peekHandoffPage();
        clearHandoff();
        const sameConnection =
          handoff.connectionId == null
          || handoff.connectionId === selectedConnection?.connectionId;
        const requested = sameConnection
          ? resolveAllowed(loaded, handoff.facilityId, (f) => f.id)
          : null;
        if (requested) {
          selectFacility(requested.id, requested.name);
          recordRecentFacility(userId, selectedConnection?.connectionId, requested.id, requested.name);
          if (page) navigate(page, { replace: true });
          return;
        }
        if (page) {
          useNotificationStore.getState().addNotification({
            message: 'You do not have access to the facility that link was for, so it was not opened.',
            type: 'warning',
            duration: 8000,
          });
        }
      }

      const match = loaded.find((f) => f.id === facilityId);
      if (match && match.name !== facilityName) {
        selectFacility(match.id, match.name);
      } else if (!match) {
        // The open facility is not in this list. This used to select loaded[0], and the list is
        // A-Z, so it quietly moved the user to whichever facility sorts first ("it keeps going to
        // Acacia", Hospitality 2026-10-13), passing over their default and the facility they last
        // used. Which facility to open is the sign-in resolver's decision
        // (resolveFacilityFromMappings): ask AuthTokenSetter to run it again. It reads the user's
        // mappings afresh, so a facility that is merely missing from this one answer stays open.
        // Asked once per facility, so two answers that disagree cannot loop.
        const key = `${selectedConnection?.connectionId ?? 0}:${userId}:${facilityId}`;
        if (reResolveAskedRef.current !== key) {
          reResolveAskedRef.current = key;
          console.warn(`[FacilityDropdown] Facility ${facilityId} is not in user ${userId}'s facility list; resolving the facility again.`);
          useGlobalStore.getState().retrySessionLoad();
        }
      }
    };
    init();
    return () => { cancelled = true; };
  }, [userId, facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (facilityOpen) {
      setTimeout(() => facilitySearchRef.current?.focus(), 100);
    } else {
      setFacilitySearch('');
    }
  }, [facilityOpen]);

  // Get recently-used facility IDs for sorting
  const recentFacilityIds = useMemo(() => {
    if (userId <= 0) return new Set<number>();
    // This connection's only: the same number is another facility in another database.
    return new Set(getRecentFacilities(userId, selectedConnection?.connectionId).map((r) => r.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, facilityOpen, selectedConnection?.connectionId]); // re-read when popover opens

  // The facility the user opens to at sign-in, starred in the list (#2072).
  // Only fetched while the switcher is open.
  const { data: myDefaultFacility = 0 } = useMyDefaultFacility(userId, facilityOpen);
  const setMyDefaultFacility = useSetMyDefaultFacility(userId);
  const toggleDefaultFacility = (facility: FacilityItem) => {
    if (setMyDefaultFacility.isPending) return;
    setMyDefaultFacility.mutate({
      facilityId: facility.id === myDefaultFacility ? 0 : facility.id,
      facilityName: facility.name,
    });
  };

  const filteredFacilities = useMemo(() => {
    let list = facilities;
    if (facilitySearch.trim()) {
      list = facilities.filter((f) => facilityMatchesSearch(f, facilitySearch));
    }
    // Sort: current facility first, then recently used, then alphabetical
    return [...list].sort((a, b) => {
      const aIsCurrent = a.id === facilityId ? 1 : 0;
      const bIsCurrent = b.id === facilityId ? 1 : 0;
      if (aIsCurrent !== bIsCurrent) return bIsCurrent - aIsCurrent;

      const aIsRecent = recentFacilityIds.has(a.id) ? 1 : 0;
      const bIsRecent = recentFacilityIds.has(b.id) ? 1 : 0;
      if (aIsRecent !== bIsRecent) return bIsRecent - aIsRecent;

      return a.name.localeCompare(b.name);
    });
  }, [facilities, facilitySearch, facilityId, recentFacilityIds]);

  // Detect if there are unsaved changes on the page.
  const hasUnsavedChanges = useCallback((): boolean => {
    // A page marked data-dirty, or one that registered its unsaved work (utils/unsavedWork.ts).
    if (hasUnsavedWork()) return true;
    // Check for any MUI dialogs that are open (likely editing something)
    const openDialogs = document.querySelectorAll('.MuiDialog-root');
    if (openDialogs.length > 0) return true;
    return false;
  }, []);

  // Global search shortcut: Ctrl+/ (Cmd+/ on Mac).
  //
  // It was Ctrl+K, which never worked: MainLayout binds Ctrl+K to the AI
  // Assistant through `useKeyboardShortcuts`, which listens on `window` in the
  // CAPTURE phase and calls `stopPropagation()` on a match. This listener is on
  // the bubble phase, so it sat downstream of that and was never reached — the
  // key opened the AI overlay every time while the magnifier's tooltip went on
  // promising search.
  //
  // Ctrl+/ is what `buildGlobalShortcuts` already declares for search, and
  // nothing else claims it.
  //
  // Ctrl+F is intercepted for the same reason, and it matters more than the shortcut nicety:
  // almost every grid in the app scrolls virtually, so only the visible rows exist in the DOM.
  // Browser Find genuinely reports "not found" for a resident who IS in the list — and the
  // natural conclusion is that the patient is missing, not that the search was wrong. Routing
  // Ctrl+F to the real search makes the answer correct instead of confidently wrong.
  useEffect(() => {
    const handleGlobalSearchKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;

      if (e.key === '/') {
        e.preventDefault();
        setGlobalSearchOpen(true);
        return;
      }

      if (e.key === 'f' || e.key === 'F') {
        // On macOS Ctrl+F is "move cursor forward" inside text fields; only Cmd+F is Find. Leave
        // the editing binding alone so typing in a note or filter box still behaves normally.
        const el = document.activeElement as HTMLElement | null;
        const editing =
          !!el &&
          (el.tagName === 'INPUT' ||
            el.tagName === 'TEXTAREA' ||
            el.isContentEditable);
        if (editing && e.ctrlKey && !e.metaKey && IS_MAC) return;

        e.preventDefault();
        setGlobalSearchOpen(true);
        showVirtualGridSearchHint();
      }
    };
    window.addEventListener('keydown', handleGlobalSearchKey);
    return () => window.removeEventListener('keydown', handleGlobalSearchKey);
  }, []);

  /**
   * Publish the two search bindings to the shortcuts registry.
   *
   * Done by hand because the listener above is by hand. It is not migrated to
   * `useKeyboardShortcuts` because of the Ctrl+F case: on macOS that combo
   * means "move cursor forward" inside a text field and must be left alone
   * there, and the shared engine calls preventDefault before the handler runs,
   * so a handler cannot decline to act. Migrating would silently break
   * cursor movement for Mac users editing a note.
   *
   * These entries are documentation only — the listener above does the work.
   */
  useEffect(() => registerShortcuts([
    {
      id: 'global-search',
      key: '/',
      ctrl: true,
      label: 'Search pages, reports and residents',
      category: 'navigation',
      handler: () => setGlobalSearchOpen(true),
    },
    {
      id: 'global-search-find',
      key: 'f',
      ctrl: true,
      label: 'Search (replaces browser Find, which misses virtualised rows)',
      category: 'navigation',
      handler: () => setGlobalSearchOpen(true),
    },
    {
      // The guide has to list the key that opens the guide, or the one command
      // someone needs when they are lost is the one command it does not
      // mention.
      //
      // Deliberately not Shift+? : that binding was removed because a stray
      // "?" in a text field popped the dialog. A function key cannot be typed,
      // which is why both of these are safe inside an input.
      id: 'keyboard-help',
      key: 'F1',
      label: 'Show keyboard shortcuts',
      category: 'general',
      allowInInput: true,
      whenModalOpen: 'fire',
      handler: () => setShortcutsOpen(true),
    },
    {
      // A real handler, not documentation with a no-op: the registry claims
      // every key it lists, so a no-op here swallowed F2 entirely.
      //
      // `whenModalOpen: 'fire'` because the default — a modal owns the
      // keyboard — is right for page shortcuts and exactly wrong for this one.
      // "Read out what is in front of me" is most needed when a dialog IS in
      // front of you; the tray ticket visualizer is a dialog, and it is the
      // main thing this command exists for.
      id: 'screen-summary',
      key: 'F2',
      label: 'Read out what is on this screen (again for detail)',
      category: 'general',
      allowInInput: true,
      whenModalOpen: 'fire',
      handler: advanceScreenSummary,
    },
  ]), []);

  // Open a facility BESIDE the current one instead of switching to it. The
  // new window gets its own sessionStorage, so both stay put — see
  // `utils/facilityWindow.ts` for why `noopener` is what makes that true.
  const openFacilityBeside = useCallback(
    (facility: FacilityItem) => {
      setFacilityAnchor(null);
      openFacilityWindow({
        connectionId: selectedConnection?.connectionId ?? null,
        facilityId: facility.id,
      });
    },
    [selectedConnection?.connectionId],
  );

  const performFacilitySwitch = useCallback(
    (facility: FacilityItem) => {
      setFacilityAnchor(null);
      setConfirmDialogOpen(false);
      setPendingFacility(null);
      switchFacility(facility.id, facility.name);
    },
    [switchFacility],
  );

  const handleSelectFacility = (facility: FacilityItem) => {
    // If selecting the same facility, just close the popover
    if (facility.id === facilityId) {
      setFacilityAnchor(null);
      return;
    }

    // Check for unsaved changes before switching
    if (hasUnsavedChanges()) {
      setPendingFacility(facility);
      setConfirmDialogOpen(true);
      setFacilityAnchor(null);
      return;
    }

    performFacilitySwitch(facility);
  };

  const handleConfirmSwitch = () => {
    if (pendingFacility) {
      performFacilitySwitch(pendingFacility);
    }
  };

  const handleCancelSwitch = () => {
    setConfirmDialogOpen(false);
    setPendingFacility(null);
  };

  const closeUserMenu = () => {
    setUserMenuAnchor(null);
    setIsDebugSubmenuOpen(false);
  };

  return (
    <>
      <header
        data-tour="top-toolbar"
        className="tt-s1 tt-bar"
      >
        {/* Three zones, in the order a person reads the bar:
              nav      where am I         (hamburger + breadcrumbs)
              search   what am I after    (exactly one search control)
              actions  tools and account  (support, alerts, facility, user)

            It was previously one flat flex row of eight siblings, so the page
            title, a filter box, a search button, a support button, a bell, a
            facility picker and an avatar all sat at the same level with
            nothing grouping them. Zones also give the responsive rules
            something to hide: below 900px the search zone collapses to its
            button, which a flat row could not express. */}
        <div className="tt-zone tt-zone--nav">
          {/* Hamburger menu button (tablet/mobile only) */}
        {onMenuClick && (
          <IconButton
            className="mobile-menu-btn"
            onClick={onMenuClick}
            aria-label="Toggle navigation menu"
            size="small"
            sx={{
              color: 'var(--text-primary)',
              minWidth: 44,
              minHeight: 44,
            }}
          >
            <i className="bi bi-list tt-s2" aria-hidden="true" />
          </IconButton>
        )}

        {/* Left: Breadcrumbs / Page Title */}
        <div className="tt-s3">
          {breadcrumbs.length > 1 ? (
            <nav
              aria-label="Breadcrumb"
              className="tt-s4"
            >
              {breadcrumbs.map((crumb, i) => {
                const isLast = i === breadcrumbs.length - 1;
                const isParent = !isLast;
                return (
                  <span
                    key={i}
                    className={`${isParent ? 'toolbar-breadcrumb-parent' : ''} tt-s5`}
                  >
                    {i > 0 && (
                      <i
                        className={`${`bi bi-chevron-right${isParent ? ' toolbar-breadcrumb-parent' : ''}`} tt-s6`}
                        aria-hidden="true"
                      />
                    )}
                    {isLast ? (
                      <span
                        aria-current="page"
                        className="tt-s7"
                      >
                        {crumb.label}
                      </span>
                    ) : (
                      <Link
                        to={crumb.path || '#'}
                        className="tt-s8"
                        onClick={crumb.onClick && ((e) => {
                          // Guarded crumb: the page owns the navigation
                          // (unsaved-changes prompt), so suppress the Link.
                          e.preventDefault();
                          crumb.onClick!();
                        })}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--text-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                      >
                        {crumb.label}
                      </Link>
                    )}
                  </span>
                );
              })}
            </nav>
          ) : pageTitle ? (
            <span className="tt-s9">
              {pageTitle}
            </span>
          ) : null}
          </div>
          {/* A page's own compact controls (ToolbarPageActions), on the
              breadcrumb row instead of a header row of their own. */}
          <ToolbarPageActionsSlot className="tt-page-actions" />
        </div>

        {/* One search control, not two.
            There used to be a text input (page filter, shown only when a page
            opted in via useToolbarSearch) sitting directly beside a magnifier
            button (global resident search). Two search affordances, adjacent,
            doing different things, with nothing to distinguish them — typing
            in the box did not search residents and clicking the magnifier did
            not filter the page.

            Now there is one box in one place. What it does depends on the
            page, and it says so: on a page that filters, it filters, and the
            shortcut chip beside it still reaches global search. On a page that
            does not, the whole box IS global search. */}
        <div className="tt-zone tt-zone--search">
          <ToolbarSearch
            placeholder={searchPlaceholder}
            query={searchQuery}
            onQueryChange={setSearchQuery}
            onOpenGlobal={() => setGlobalSearchOpen(true)}
            residentPluralLower={residentPluralLower}
            globalLabel={globalSearchLabel(residentPluralLower, {
              items: roleLoaded && itemsLevel >= 1,
              recipes: roleLoaded && recipeLevel >= 1,
            })}
          />
        </div>

        <div className="tt-zone tt-zone--actions">
        {/* Help for this page: its article, or the help list for its area (help/pageHelp.ts). */}
        <PageHelpButton />

        {/* Test Kitchen: report a problem with the current screen. Renders nothing when the
            beta flag is off, and is dropped from the bundle entirely at build time. On a phone
            the More sheet opens it, so the bar keeps only the dialog. */}
        <ReportProblemToolbarButton trigger={!isMobile} />

        {/* Cobrowse Support Button (on a phone: in the More sheet, and here only while live) */}
        {(!isMobile || supportSessionLive) && <CobrowseButton />}

        {/* Notification Bell */}
        <span data-tour="notification-bell">
          <NotificationBell />
        </span>

        {/* Right: Facility Selector -- shows building icon + facility name prominently */}
        <Tooltip title="Switch facility" arrow>
          <div
            className="toolbar-facility-selector tt-s14"
            onClick={(e) => setFacilityAnchor(e.currentTarget)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setFacilityAnchor(e.currentTarget as HTMLElement);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Current facility: ${facilityName || 'None selected'}. Click to switch.`}
            aria-haspopup="true"
            aria-expanded={facilityOpen}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--text-secondary)';
              e.currentTarget.style.background = 'rgba(102,126,234,0.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.background = 'var(--toolbar-bg)';
            }}
          >
            {/* Per-facility colour, so two windows are told apart at a
                glance instead of by reading. Decorative and aria-hidden --
                the NAME beside it is the actual answer, and nothing here is
                conveyed by colour alone. */}
            <span
              className="tt-facility-accent"
              style={{ background: facilityAccentColor(facilityId) }}
              aria-hidden="true"
            />
            <i
              className="bi bi-building tt-s15"
              aria-hidden="true"
            />
            <span
              className="facility-name-text tt-s16"
            >
              {facilityName || 'Select Facility'}
            </span>
            <i
              className="bi bi-chevron-down tt-s17"
              aria-hidden="true"
            />
          </div>
        </Tooltip>

        {/* Facility Panel Popover */}
        <Popover
          open={facilityOpen}
          anchorEl={facilityAnchor}
          onClose={() => setFacilityAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          // Phones: the screen less an 8px gutter (.tt-popover), not MUI's 16px.
          marginThreshold={isMobile ? 8 : 16}
          slotProps={{
            paper: {
              className: 'tt-popover',
              sx: {
                width: 420,
                maxHeight: 520,
                mt: 1,
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              },
            },
          }}
        >
          <div
            className="tt-s18"
          >
            <span className="tt-s19">Switch Facility</span>
            <i
              className="bi bi-x-lg tt-s20"
              role="button"
              tabIndex={0}
              aria-label="Close facility selector"
              onClick={() => setFacilityAnchor(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setFacilityAnchor(null);
              }}
            />
          </div>

          <div
            className="tt-s21"
          >
            <i className="bi bi-search tt-s22" aria-hidden="true" />
            <input
              ref={facilitySearchRef}
              type="text"
              placeholder="Search facilities..."
              value={facilitySearch}
              onChange={(e) => setFacilitySearch(e.target.value)}
              className="tt-s23"
            />
            {facilitySearch && (
              <i
                className="bi bi-x-circle tt-s24"
                role="button"
                tabIndex={0}
                aria-label="Clear search"
                onClick={() => setFacilitySearch('')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setFacilitySearch('');
                }}
              />
            )}
          </div>

          <div className="tt-s25">
            {loadingFacilities ? (
              <div className="tt-s26">
                <CircularProgress size={28} />
                <div className="tt-s27">
                  Loading facilities...
                </div>
              </div>
            ) : facilitiesError ? (
              <div className="tt-s28">
                <i
                  className="bi bi-exclamation-triangle tt-s29"
                  aria-hidden="true"
                />
                <div className="tt-s30">{facilitiesError}</div>
                <button
                  onClick={() => loadFacilities()}
                  className="tt-s31"
                >
                  Retry
                </button>
              </div>
            ) : filteredFacilities.length === 0 && facilities.length === 0 ? (
              <div className="tt-s32">
                <i
                  className="bi bi-building tt-s33"
                  aria-hidden="true"
                />
                <div className="tt-s30">No facilities available</div>
              </div>
            ) : filteredFacilities.length === 0 ? (
              <div className="tt-s32">
                <i
                  className="bi bi-search tt-s34"
                  aria-hidden="true"
                />
                <span className="tt-s30">No facilities match your search</span>
              </div>
            ) : (
              <>
                {/* Section: Recently Used (only when not searching) */}
                {!facilitySearch.trim() &&
                  filteredFacilities.some(
                    (f) => recentFacilityIds.has(f.id) && f.id !== facilityId,
                  ) && (
                    <>
                      <div
                        className="tt-s35"
                      >
                        <i
                          className="bi bi-clock-history tt-s36"
                          aria-hidden="true"
                        />
                        Recently Used
                      </div>
                      {filteredFacilities
                        .filter((f) => recentFacilityIds.has(f.id) && f.id !== facilityId)
                        .map((facility) => (
                          <FacilityListItem
                            key={`recent-${facility.id}`}
                            facility={facility}
                            isSelected={false}
                            isRecent
                            isDefault={facility.id === myDefaultFacility}
                            onToggleDefault={() => toggleDefaultFacility(facility)}
                            onClick={() => handleSelectFacility(facility)}
                            onOpenBeside={() => openFacilityBeside(facility)}
                          />
                        ))}
                      <div
                        className="tt-s37"
                      />
                    </>
                  )}

                {/* Section: Current + All Facilities */}
                <div
                  className="tt-s35"
                >
                  {facilitySearch.trim() ? 'Search Results' : 'All Facilities'}
                </div>
                {filteredFacilities.map((facility) => {
                  const isSelected = facilityId === facility.id;
                  const isRecent = recentFacilityIds.has(facility.id);
                  return (
                    <FacilityListItem
                      key={facility.id}
                      facility={facility}
                      isSelected={isSelected}
                      isRecent={isRecent}
                      isDefault={facility.id === myDefaultFacility}
                      onToggleDefault={() => toggleDefaultFacility(facility)}
                      onClick={() => handleSelectFacility(facility)}
                      onOpenBeside={() => openFacilityBeside(facility)}
                    />
                  );
                })}
              </>
            )}
          </div>

          {/* Footer: facility count + the "somewhere else entirely" escape
              hatch. The per-row button opens another facility in THIS
              organization; support staff hopping between customers need a
              different connection, which starts at the picker. */}
          <div className="tt-facility-footer">
            {facilities.length > 0 && (
              <span className="tt-facility-footer-count">
                {filteredFacilities.length} of {facilities.length} facilities
              </span>
            )}
            <button
              type="button"
              className="tt-facility-footer-action"
              onClick={() => {
                setFacilityAnchor(null);
                openFacilityWindow();
              }}
            >
              <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
              Open another organization in a new window
            </button>
          </div>
        </Popover>

        {/* Temp Menu Override Indicator (on a phone: a dot on the avatar and a line in More) */}
        {tempMenuOverrideId && !isMobile && (
          <Tooltip title={`Temp Menu: ${tempMenuOverrideName}`} arrow>
            <div
              className="tt-s39"
            >
              <i className="bi bi-journal-text tt-s40" aria-hidden="true" />
              <span className="toolbar-user-name">{tempMenuOverrideName}</span>
            </div>
          </Tooltip>
        )}

        {/* User Identity */}
        <div
          onClick={(e) => setUserMenuAnchor(e.currentTarget)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setUserMenuAnchor(e.currentTarget as HTMLElement);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`User menu for ${userName}${updateNudge ? ', update ready' : ''}${isMobile && tempMenuOverrideId ? `, Temp Menu ${tempMenuOverrideName}` : ''}`}
          aria-haspopup="true"
          aria-expanded={userMenuOpen}
          className="tt-s41"
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0,0,0,0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <div
            className="tt-s42"
          >
            {userInitials}
            {updateNudge && <span className="tt-update-dot" aria-hidden="true" />}
            {isMobile && tempMenuOverrideId && <span className="tt-temp-dot" aria-hidden="true" />}
          </div>
          <span className="toolbar-user-name tt-s43">
            {userName}
          </span>
          <i
            className="bi bi-chevron-down toolbar-user-name tt-s44"
            aria-hidden="true"
          />
        </div>

        {/* User Menu Popover */}
        <Popover
          open={userMenuOpen}
          anchorEl={userMenuAnchor}
          onClose={closeUserMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              // On open, MUI focuses the paper. Unnamed, a screen reader reads
              // its entire contents as one string — "U User Role: ... Facility:
              // ... Appearance UI Size" — before she can do anything. Named, it
              // announces "User menu, dialog" and she tabs through the controls,
              // which are individually well labelled already.
              //
              // dialog rather than menu: this holds radio groups and toggle
              // buttons, not a list of commands, and menu semantics would
              // promise arrow-key navigation that a Popover does not provide.
              role: 'dialog',
              'aria-label': 'User menu',
              sx: {
                width: 300,
                mt: 1,
                borderRadius: 2,
                maxHeight: 'calc(100vh - 80px)',
                overflowY: 'auto',
              },
            },
          }}
        >
          <div className="tt-s45">
            <div className="tt-s46">
              <div
                className="tt-s47"
              >
                {userInitials}
              </div>
              <div className="tt-s48">
                <div
                  className="tt-s49"
                >
                  {userName}
                </div>
                {userEmail && (
                  <div
                    className="tt-s50"
                  >
                    {userEmail}
                  </div>
                )}
              </div>
            </div>
            {/* Role & Facility info */}
            <div className="tt-s51">
              {userRole?.name && (
                <div
                  className="tt-s52"
                >
                  <i
                    className="bi bi-shield-check tt-s53"
                    aria-hidden="true"
                  />
                  <span>
                    Role:{' '}
                    <span className="tt-s54">
                      {userRole.name}
                    </span>
                  </span>
                </div>
              )}
              {facilityName && (
                <div
                  className="tt-s52"
                >
                  <i
                    className="bi bi-building tt-s53"
                    aria-hidden="true"
                  />
                  <span>
                    Facility:{' '}
                    <span className="tt-s54">
                      {facilityName}
                    </span>
                  </span>
                </div>
              )}
              {globalUserName && globalUserName !== userName && (
                <div
                  className="tt-s52"
                >
                  <i
                    className="bi bi-person tt-s53"
                    aria-hidden="true"
                  />
                  <span>
                    Username:{' '}
                    <span className="tt-s54">
                      {globalUserName}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="tt-s55" />

          <div className="tt-s56">
            <div
              className="tt-s57"
            >
              Appearance
            </div>
            <div className="tt-s58">UI Size</div>
            <div
              className="tt-s59"
            >
              {UI_SIZE_ORDER.map((size) => (
                <button
                  key={size}
                  onClick={() => { setThemeSize(size); noteSizeChoice(themeSize, size); }}
                  style={{
                    padding: '6px 8px',
                    border: `2px solid ${themeSize === size ? '#0078d4' : 'var(--border-color)'}`,
                    borderRadius: 4,
                    background: themeSize === size ? '#0078d4' : 'transparent',
                    color: themeSize === size ? '#fff' : 'inherit',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-small)',
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {UI_SIZE_LABELS[size]}
                </button>
              ))}
            </div>
            <div className="tt-s58" id="tt-font-label">Font</div>
            <div className="tt-font-grid" role="group" aria-labelledby="tt-font-label">
              {APP_FONTS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={font === f.key}
                  onClick={() => setFont(f.key)}
                  className={`tt-font-choice${font === f.key ? ' active' : ''}`}
                  style={{ fontFamily: f.stack }}
                >
                  {f.name}
                </button>
              ))}
            </div>
            <div className="tt-s58">Mode</div>
            <RadioGroupField
              className="tt-s60"
              ariaLabel="Mode"
              name="themeMode"
              value={darkMode}
              onValueChanged={setDarkMode}
              options={THEME_MODE_OPTIONS}
            />

            {/*
              High Contrast sits here, beside Mode and UI Size, because it is
              the same kind of decision and this menu is where people look for
              it. It shipped only on the Settings page at first and was not
              found.

              A checkbox rather than a third pair of radios: it layers on top
              of Light or Dark rather than being a third mode, and a radio
              group would imply otherwise.
            */}
            <div className="tt-s58">Contrast</div>
            <CheckboxField
              className="tt-s60"
              label={<><i className="bi bi-circle-half tt-s30" aria-hidden="true" /> High Contrast</>}
              value={highContrast}
              onValueChanged={setHighContrast}
            />
          </div>

          <div className="tt-s55" />

          {/*
            Temp Menu stays in this menu rather than moving to `/settings` —
            it is session state, not a saved preference, and a settings page
            is the wrong home for something that dies with the session.

            It is collapsed because it costs five rows to say "not overridden",
            which is the answer almost every time. Forgetting an active
            override is already covered: the toolbar shows a chip naming the
            menu whenever one is set, so the state is visible with this menu
            shut. Opens by default when an override IS active, so the control
            that clears it is one click away rather than two.
          */}
          <div className="tt-s56">
            <div
              onClick={() => setIsTempMenuOpen(!isTempMenuOpen)}
              role="button"
              tabIndex={0}
              aria-expanded={isTempMenuOpen}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsTempMenuOpen(!isTempMenuOpen);
                }
              }}
              className="tt-temp-menu-summary"
            >
              <span className="tt-s57">Temp Menu</span>
              <span className="tt-temp-menu-value">
                {tempMenuOverrideName || 'Use Active Menu'}
              </span>
              <i
                className={`bi bi-chevron-${isTempMenuOpen ? 'down' : 'right'} tt-s72`}
                aria-hidden="true"
              />
            </div>
            {isTempMenuOpen && (
            <>
            <div className="tt-s63">
              Temporarily override the active menu cycle for your session only.
            </div>
            <div className="tt-s64">
              <SelectField<MenuHeader>
                className="app-field-grow"
                ariaLabel="Temp Menu"
                dataSource={availableMenus}
                displayExpr="menu_Name"
                valueExpr="pKey_Menu_Hdr"
                value={tempMenuOverrideId ?? null}
                placeholder="- Use Active Menu -"
                showClearButton={false}
                onValueChanged={(val) => {
                  if (val == null || val === '') {
                    clearTempMenuOverride();
                  } else {
                    const id = Number(val);
                    const menu = availableMenus.find((m: MenuHeader) => m.pKey_Menu_Hdr === id);
                    setTempMenuOverride(id, menu?.menu_Name ?? '');
                  }
                }}
              />
              {tempMenuOverrideId && (
                <button
                  onClick={() => clearTempMenuOverride()}
                  title="Clear override"
                  className="tt-s65"
                >
                  <i className="bi bi-x-lg tt-s40" aria-hidden="true" />
                </button>
              )}
            </div>
            {tempMenuOverrideId && (
              <div
                className="tt-s66"
              >
                <i className="bi bi-exclamation-triangle tt-s40" aria-hidden="true" />
                <span>
                  Override active: <strong>{tempMenuOverrideName}</strong>
                </span>
              </div>
            )}
            </>
            )}
          </div>

          <div className="tt-s55" />

          {/*
            Facilities, Tables, Users, Roles, Logged In and User Settings used
            to be listed here individually, which is how this menu turned into
            the admin nav — every new admin page got a row. They all live on
            `/settings` now, which gates each one by the same security field
            its route is secured with.
          */}
          {updateNudge && (
            <>
              <div className="tt-s67">
                <UserMenuItem
                  icon="bi-arrow-repeat"
                  label="Update ready · Refresh"
                  onClick={() => {
                    closeUserMenu();
                    applyUpdate();
                  }}
                />
              </div>
              <div className="tt-s55" />
            </>
          )}

          {/* Your own settings, from your own name. This said "Settings" and
              opened the Setup section (facility-wide pages, most of them admin
              only), so the one thing everyone comes here for took a second
              click on a page they may not otherwise use. Setup is in the side
              menu. */}
          <div className="tt-s67">
            <UserMenuItem
              icon="bi-person-gear"
              label="My Settings"
              onClick={() => {
                closeUserMenu();
                navigate('/user-settings');
              }}
            />
          </div>

          <div className="tt-s55" />

          {/*
            Contact Us, Privacy Policy, Tutorials and What's New were four rows
            behind a disclosure triangle, and each one only set `?tab=` on the
            same page — a slower table of contents for a page that already has
            those tabs down its side.

            About stays a separate row because it is not that page: the dialog
            reports the facility, web version, resolved API endpoint and React
            build, which is what support asks for on a call. `/help?tab=about`
            is the company address and phone. Different content, both wanted.
          */}
          <div className="tt-s67">
            <UserMenuItem
              icon="bi-question-circle"
              label="Help"
              onClick={() => {
                closeUserMenu();
                navigate('/help');
              }}
            />
            <UserMenuItem
              icon="bi-mortarboard"
              label="Guided Tours"
              onClick={() => {
                closeUserMenu();
                useTourStore.getState().openTourList();
              }}
            />
            <UserMenuItem
              icon="bi-info-circle"
              label="About Dietech"
              onClick={() => {
                closeUserMenu();
                setAboutOpen(true);
              }}
            />
          </div>

          {/*
            Debug was visible to everyone. "Run Migrations" alters the facility
            schema and "Clear Current Connection" drops the routing the session
            is running on — neither is something a dietary aide should find by
            opening their own avatar menu. Dev keeps it unconditionally; in a
            deployed build it is PSI staff only (a psibuffalo.com login), the
            same rule the API enforces on Migration/Run. It used to want
            User_Level 6, which no role has, so nobody could reach it. Nothing
            is orphaned: Change Connection is on the bottom bar for everyone.
          */}
          {(import.meta.env.DEV || isPsiEmail(userEmail)) && (
          <>
          <div className="tt-s55" />

          <div className="tt-s69">
            <div
              onClick={() => setIsDebugSubmenuOpen(!isDebugSubmenuOpen)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ')
                  setIsDebugSubmenuOpen(!isDebugSubmenuOpen);
              }}
              className="tt-s70"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0,0,0,0.04)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <i className="bi bi-bug tt-s30" aria-hidden="true" />
              <span className="tt-s71">Debug</span>
              <i
                className={`${`bi bi-chevron-${isDebugSubmenuOpen ? 'down' : 'right'}`} tt-s72`}
                aria-hidden="true"
              />
            </div>
            {isDebugSubmenuOpen && (
              <div className="tt-s73">
                <div
                  className="tt-s74"
                >
                  <i className="bi bi-info-circle tt-s40" aria-hidden="true" />
                  <span>
                    {facilityName} | User {userId}
                  </span>
                </div>
                <UserMenuItem
                  icon="bi-gear"
                  label="Debug Settings"
                  onClick={() => {
                    closeUserMenu();
                  }}
                />
                <UserMenuItem
                  icon="bi-x-circle"
                  label="Clear Current Connection"
                  onClick={() => {
                    closeUserMenu();
                    clearConnection();
                    // switch=true: show the list, even to a user with one organization.
                    navigate('/connection?switch=true');
                  }}
                />
                <UserMenuItem
                  icon="bi-arrow-left-right"
                  label="Change Connection"
                  onClick={() => {
                    closeUserMenu();
                    navigate('/connection?switch=true');
                  }}
                />
                <UserMenuItem
                  icon="bi-database-gear"
                  label="Run Migrations"
                  onClick={() => {
                    closeUserMenu();
                    setMigrationsOpen(true);
                  }}
                />
              </div>
            )}
          </div>
          </>
          )}

          <div className="tt-s55" />

          <div className="tt-s69">
            <div
              onClick={() => {
                closeUserMenu();
                signOut(auth);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  closeUserMenu();
                  signOut(auth);
                }
              }}
              className="tt-s75"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239,68,68,0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <i className="bi bi-box-arrow-right tt-s30" aria-hidden="true" />
              <span>Log Out</span>
            </div>
          </div>
        </Popover>
        </div>
      </header>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelSwitch} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 'var(--font-size-large)' }}>Unsaved Changes</DialogTitle>
        <DialogContent>
          <p className="tt-s76">
            You may have unsaved changes on this page. Switching facilities will discard any
            unsaved work.
          </p>
          {pendingFacility && (
            <p className="tt-s77">
              Switch to:{' '}
              <strong className="tt-s78">{pendingFacility.name}</strong>
            </p>
          )}
        </DialogContent>
        {/* Switching discards unsaved work: Enter never confirms it. */}
        <DialogActionsBar
          onCancel={handleCancelSwitch}
          onSave={handleConfirmSwitch}
          saveLabel="Switch Anyway"
          saveColor="warning"
          keyboard={false}
          sx={{ padding: '8px 24px 16px' }}
        />
      </Dialog>

      {/* Run Migrations: Preview, then Run (PSI only; the API enforces it too) */}
      <RunMigrationsDialog open={migrationsOpen} onClose={() => setMigrationsOpen(false)} get={(url) => apiClient.get(url)} />

      {/* About Dialog */}
      <Dialog open={aboutOpen} onClose={() => setAboutOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 'var(--font-size-large)', display: 'flex', alignItems: 'center', gap: 1 }}>
          <i className="bi bi-info-circle tt-s79" />
          About Dietech
        </DialogTitle>
        <DialogContent>
          <div className="tt-s80">
            <div className="tt-s81">
              <span>Facility</span>
              <strong className="tt-s78">{selectedConnection?.facilityName || '-'}</strong>
            </div>
            <div className="tt-s81">
              <span>Web Version</span>
              <strong className="tt-s78">{selectedConnection?.webVersion || 'Default'}</strong>
            </div>
            <div className="tt-s82">
              <span>API Endpoint</span>
              <strong className="tt-s83">{getRoutedApiHost(selectedConnection?.webVersion)}</strong>
            </div>
            <div className="tt-s81">
              <span>React Build</span>
              <strong className="tt-s78">{import.meta.env.VITE_APP_VERSION || 'Production'}</strong>
            </div>
            <div className="tt-s37" />
            <div className="tt-s84">
              Dietech Software &copy; {new Date().getFullYear()} PSI Buffalo
            </div>
          </div>
        </DialogContent>
        <DialogActionsBar
          onCancel={() => setAboutOpen(false)}
          cancelLabel="Close"
          leftSlot={
            <>
              <Button
                variant="outlined"
                sx={SECONDARY_BUTTON_SX}
                onClick={() => { setAboutOpen(false); setFeatureAccessOpen(true); }}
                startIcon={<i className="bi bi-grid-3x3-gap tt-s86" />}
              >
                Show All Features
              </Button>
              <Button
                variant="outlined"
                sx={SECONDARY_BUTTON_SX}
                onClick={() => { setAboutOpen(false); setShortcutsOpen(true); }}
                startIcon={<i className="bi bi-keyboard tt-s86" />}
              >
                Keyboard Shortcuts
              </Button>
            </>
          }
          sx={{ px: 3 }}
        />
      </Dialog>

      <FeatureAccessDialog open={featureAccessOpen} onClose={() => setFeatureAccessOpen(false)} />

      <KeyboardShortcutsDialog
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
        shortcuts={documentedShortcuts}
      />

      <GlobalSearch open={globalSearchOpen} onClose={() => setGlobalSearchOpen(false)} />
    </>
  );
}

/** Individual facility row in the selector popover */
function FacilityListItem({
  facility,
  isSelected,
  isRecent,
  onClick,
  onOpenBeside,
  isDefault = false,
  onToggleDefault,
}: {
  facility: FacilityItem;
  isSelected: boolean;
  isRecent: boolean;
  onClick: () => void;
  /** This is the facility the user opens to at sign-in. */
  isDefault?: boolean;
  /** Make this the sign-in facility, or clear it if it already is. */
  onToggleDefault?: () => void;
  /** Open this facility in a second window instead of switching to it. */
  onOpenBeside?: () => void;
}) {
  return (
    // The row and the new-window button are SIBLINGS, not nested. The row is a
    // `role="button"` div, and putting a real button inside one leaves screen
    // reader users with an interactive control they cannot reach.
    <div className="tt-facility-row">
    <div
      className="tt-facility-row-main"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      aria-label={`${isSelected ? 'Current facility: ' : ''}${facility.name}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        borderRadius: 6,
        cursor: 'pointer',
        flex: 1,
        minWidth: 0,
        background: isSelected ? 'rgba(102,126,234,0.1)' : 'transparent',
        transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.background = 'rgba(0,0,0,0.04)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isSelected ? 'rgba(102,126,234,0.1)' : 'transparent';
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: isSelected
            ? 'var(--brand-gradient)'
            : '#e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <i
          className={`bi ${facility.isCorporate ? 'bi-buildings' : 'bi-building'}`}
          style={{ fontSize: 'var(--icon-size)', color: isSelected ? '#fff' : '#666' }}
          aria-hidden="true"
        />
      </div>
      <div className="tt-s48">
        <div
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: isSelected ? 600 : 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {facility.name}
        </div>
        <div
          className="tt-s87"
        >
          {facility.isCorporate && (
            <span className="tt-s88">
              Corporate
            </span>
          )}
          {isDefault && (
            <span className="tt-s88">
              <i className="bi bi-star-fill tt-s89" aria-hidden="true" />
              Default
            </span>
          )}
          {isRecent && !isSelected && (
            <span className="tt-s88">
              <i
                className="bi bi-clock-history tt-s89"
                aria-hidden="true"
              />
              Recent
            </span>
          )}
        </div>
      </div>
      {isSelected && (
        <div className="tt-s90">
          <span
            className="tt-s91"
          >
            Current
          </span>
          <i
            className="bi bi-check-circle-fill tt-s92"
            aria-hidden="true"
          />
        </div>
      )}
    </div>
    {onToggleDefault && (
      <Tooltip title={isDefault ? 'Your default facility (click to clear)' : 'Make this my default facility'} arrow>
        <button
          type="button"
          className={`tt-facility-row-open${isDefault ? ' is-default' : ''}`}
          aria-label={isDefault ? `${facility.name} is your default facility` : `Make ${facility.name} my default facility`}
          aria-pressed={isDefault}
          onClick={onToggleDefault}
        >
          <i className={`bi ${isDefault ? 'bi-star-fill' : 'bi-star'}`} aria-hidden="true" />
        </button>
      </Tooltip>
    )}
    {onOpenBeside && (
      <Tooltip title="Open in a new window" arrow>
        <button
          type="button"
          className="tt-facility-row-open"
          // Named in full, because a row of identical "open in a new window"
          // buttons tells a screen reader user nothing about which is which.
          aria-label={`Open ${facility.name} in a new window`}
          onClick={onOpenBeside}
        >
          <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
        </button>
      </Tooltip>
    )}
    </div>
  );
}

function UserMenuItem({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      className="tt-s70"
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(0,0,0,0.04)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <i
        className={`${`bi ${icon}`} tt-s93`}
        aria-hidden="true"
      />
      <span>{label}</span>
    </div>
  );
}
